import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api/client";

function todayMinus(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function FinanceLanding() {
  const [from, setFrom] = useState(todayMinus(7));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  function trigger(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const exportCsv = useMutation({
    mutationFn: () => api.admin.payrollExportUrl({ from, to }),
    onSuccess: ({ blob, filename }) => { trigger(blob, filename); setError(null); },
    onError: (e) => setError((e as Error).message),
  });

  const exportZip = useMutation({
    mutationFn: () => api.admin.payrollExportZipUrl({ from, to }),
    onSuccess: ({ blob, filename }) => { trigger(blob, filename); setError(null); },
    onError: (e) => setError((e as Error).message),
  });

  const exportMitch = useMutation({
    mutationFn: () => api.admin.payrollExportMitchXlsxUrl({ from, to }),
    onSuccess: ({ blob, filename }) => { trigger(blob, filename); setError(null); },
    onError: (e) => setError((e as Error).message),
  });

  const exportBacs = useMutation({
    mutationFn: () => api.admin.payrollExportBacsCsvUrl({ from, to }),
    onSuccess: ({ blob, filename }) => { trigger(blob, filename); setError(null); },
    onError: (e) => setError((e as Error).message),
  });

  const exportIntacct = useMutation({
    mutationFn: () => api.admin.payrollExportIntacctZipUrl({ from, to }),
    onSuccess: ({ blob, filename }) => { trigger(blob, filename); setError(null); },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Finance</h1>
        <p className="text-slate-600 mt-1">Payroll exports + cross-home reports.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
        <h2 className="font-medium text-liaise-navy">Payroll export</h2>
        <p className="text-slate-600 text-sm">
          One row per approved line, with claimant, employee number, cost centre, category, net/VAT/gross, and dates. Filter by approved-between date — drop into payroll.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">Approved from</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">Approved to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>
        </div>

        <div className="flex gap-3 items-center pt-2 flex-wrap">
          <button
            onClick={() => exportMitch.mutate()}
            disabled={exportCsv.isPending || exportZip.isPending || exportMitch.isPending}
            className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
            title="Two-tab xlsx for Payroll: Expenses (line-level) + Bank Details (claimant totals)."
          >
            {exportMitch.isPending ? "Building xlsx…" : "Download payroll xlsx"}
          </button>
          <button
            onClick={() => exportCsv.mutate()}
            disabled={exportCsv.isPending || exportZip.isPending || exportMitch.isPending}
            className="bg-white border border-liaise-navy text-liaise-navy font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy/5 disabled:opacity-50"
            title="Detailed CSV with every column (employee #, VAT split, mileage, status timestamps)."
          >
            {exportCsv.isPending ? "Generating…" : "Download detailed CSV"}
          </button>
          <button
            onClick={() => exportZip.mutate()}
            disabled={exportCsv.isPending || exportZip.isPending || exportMitch.isPending || exportBacs.isPending}
            className="bg-white border border-liaise-navy text-liaise-navy font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy/5 disabled:opacity-50"
            title="Detailed CSV plus a receipts/ folder containing every attached receipt for the claims in this window."
          >
            {exportZip.isPending ? "Building zip…" : "Download CSV + receipts (zip)"}
          </button>
          <button
            onClick={() => exportIntacct.mutate()}
            disabled={exportCsv.isPending || exportZip.isPending || exportMitch.isPending || exportBacs.isPending || exportIntacct.isPending}
            className="bg-white border border-slate-300 text-slate-700 font-medium px-5 py-2.5 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            title="Six CSVs (one per Liaise legal entity) in Sage Intacct AP-Bill import format. Ready for the Finance team's Intacct upload. Vendor IDs blank until the supplier map is populated."
          >
            {exportIntacct.isPending ? "Building zip…" : "Download Intacct CSVs (zip)"}
          </button>
          <button
            onClick={() => exportBacs.mutate()}
            disabled={exportCsv.isPending || exportZip.isPending || exportMitch.isPending || exportBacs.isPending || exportIntacct.isPending}
            className="bg-white border border-slate-300 text-slate-700 font-medium px-5 py-2.5 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            title="BACS multi-payment CSV (notepad-compatible). Sort code and account number are blank for now — finance to fill in until bank-details join is wired."
          >
            {exportBacs.isPending ? "Generating…" : "Download BACS CSV"}
          </button>
          {error && <span className="text-red-600 text-sm">{error}</span>}
        </div>
        <p className="text-xs text-slate-500 pt-1">
          <strong>Payroll xlsx</strong>: 2-tab file for Payroll (Expenses + Bank Details). <strong>Intacct CSVs (zip)</strong>: 6 entity CSVs ready for Finance to import directly into Sage. <strong>BACS CSV</strong>: notepad-format payment file for the bank. <strong>Detailed CSV</strong>: every column for archive and audit. Supplier IDs and BACS sort/account are blank until those data sources are wired.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <Link to="/finance/reports" className="font-medium text-liaise-navy hover:underline">
          Reports →
        </Link>
        <p className="text-slate-600 text-sm mt-1">
          Spend by home, region, category, claimant. Date-range filtered.
        </p>
      </div>
    </div>
  );
}
